import { Box } from '@mui/material';
import { Outlet, useLocation, useParams } from 'react-router';
import { useGetCatalogQuery, useListPhasesQuery } from '../../api/content.api';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { LifecycleRail, LifecycleRailSkeleton } from '../../components/LifecycleRail';
import { ReturnLink } from '../../components/ReturnLink';
import { buildSdlcTrail } from '../../content';
import { contentWidthSx, pageGutterSx } from '../../theme';
import { pageEnterSx } from '../../theme/transitions';

/**
 * The frame every `/sdlc` page renders inside: the gutters, the width cap, and
 * everything that says where you are — the back link, the breadcrumb trail and
 * the lifecycle rail, in that order.
 *
 * **All three live here rather than on three pages, and the ordering is why.**
 * The designs put the back link and the trail *above* the rail; with the rail in
 * the layout and the link on the page, the link could only render below it.
 *
 * **The trail is why the catalog is fetched here.** It needs the *names* of a
 * method's ancestors, which no per-page endpoint returns — so one 260KB request
 * covers the whole section rather than one per page.
 *
 * **The rail is hidden on the L1 grid**, which already is the lifecycle.
 *
 * A failure to load either query is silent *here*: this is navigation, and the
 * page below already reports whatever went wrong with its own content. The trail
 * falls back to prettified slugs and the rail draws nothing.
 */
export const SdlcLayout = () => {
  const { phaseSlug, processSlug, methodSlug } = useParams();
  const params = { phaseSlug, processSlug, methodSlug };
  const { data: phases, isLoading: phasesLoading } = useListPhasesQuery();
  const { data: catalog } = useGetCatalogQuery();
  const { pathname } = useLocation();

  const { crumbs, returnTo } = buildSdlcTrail(params, phases ?? [], catalog ?? []);
  const isDeepPage = phaseSlug !== undefined;

  return (
    <Box sx={{ ...pageGutterSx, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ ...contentWidthSx, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {isDeepPage && (
          <Box sx={{ paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {returnTo !== undefined && <ReturnLink to={returnTo.to} label={returnTo.label} />}

            <Breadcrumbs crumbs={crumbs} />

            {/*
              More air above the rail than between the back link and the trail,
              so those two text lines read as one group and the rail as a
              separate band under them.
            */}
            <Box sx={{ marginTop: 4 }}>
              {phasesLoading ? (
                <LifecycleRailSkeleton />
              ) : (
                <LifecycleRail phases={phases ?? []} activePhaseSlug={phaseSlug} />
              )}
            </Box>
          </Box>
        )}

        {/*
          Keyed on the path so the container remounts on every navigation and
          the entry animation runs from the start, with no effect deciding
          when. It wraps only the outlet: the back link, trail and rail are the
          same on both pages, and animating them would make the chrome flicker
          on a move that did not change it.
        */}
        <Box key={pathname} sx={pageEnterSx}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};
